import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../contexts/AuthContext';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

type ProgrammationFormData = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  category: string;
};

const ProgrammationForm: React.FC = () => {
  const { user } = useAuthContext();
  const currentUser = user;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<ProgrammationFormData>({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    category: 'senior',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Vérifier que l'utilisateur est connecté
    if (!currentUser) {
      setError('Vous devez être connecté pour créer une programmation');
      return;
    }

    // Validation des dates
    if (!formData.startDate || !formData.endDate) {
      setError('Les dates de début et de fin sont requises');
      return;
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    
    if (startDate >= endDate) {
      setError('La date de fin doit être postérieure à la date de début');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      // Ajouter la programmation à Firestore
      await addDoc(collection(db, 'programmations'), {
        title: formData.title,
        description: formData.description,
        startDate: startDate,
        endDate: endDate,
        category: formData.category,
        userId: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPublic: true,
      });

      // Rediriger vers le tableau de bord
      navigate('/dashboard');
    } catch (err) {
      console.error('Erreur lors de la création de la programmation:', err);
      setError('Une erreur est survenue lors de la création de la programmation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 p-4">
      {error && (
        <div className="mb-6 bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="flex items-center gap-2">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Titre de la planification *
          </label>
          <input
            type="text"
            name="title"
            id="title"
            required
            value={formData.title}
            onChange={handleChange}
            placeholder="Ex : Préparation estivale U15"
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Catégorie *
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
            style={{
              backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"%3E%3Cpath stroke="%239ca3af" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 8l4 4 4-4"/%3E%3C/svg%3E")',
              backgroundPosition: 'right 0.75rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.25em 1.25em',
              paddingRight: '2.5rem',
              minHeight: '3rem',
            }}
          >
            <optgroup label="Séniors">
              <option value="senior">Séniors</option>
            </optgroup>
            <optgroup label="U19 - U16" className="text-blue-300">
              <option value="u19">U19</option>
              <option value="u18">U18</option>
              <option value="u17">U17</option>
              <option value="u16">U16</option>
            </optgroup>
            <optgroup label="U15 - U12">
              <option value="u15">U15</option>
              <option value="u14">U14</option>
              <option value="u13">U13</option>
              <option value="u12">U12</option>
            </optgroup>
            <optgroup label="U11 - U8">
              <option value="u11">U11</option>
              <option value="u10">U10</option>
              <option value="u9">U9</option>
              <option value="u8">U8</option>
            </optgroup>
            <optgroup label="U7 - U6">
              <option value="u7">U7</option>
              <option value="u6">U6</option>
            </optgroup>
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Date de début *
            </label>
            <input
              type="date"
              name="startDate"
              id="startDate"
              required
              value={formData.startDate}
              onChange={handleChange}
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin *
            </label>
            <input
              type="date"
              name="endDate"
              id="endDate"
              required
              value={formData.endDate}
              onChange={handleChange}
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            rows={4}
            value={formData.description}
            onChange={handleChange}
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Décrivez les objectifs de cette planification..."
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-300"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Création...' : 'Créer la planification'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProgrammationForm;
